import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

interface UseAutoScrollOptions {
  /** Distance from bottom (px) to consider "at bottom". Default: 80 */
  threshold?: number;
  /** Smooth scroll for new messages vs instant for programmatic jumps. Default: true */
  smooth?: boolean;
  /** When true, suppress auto-scroll (e.g. during keyboard open/close). Default: false */
  isKeyboardTransitioning?: boolean;
}

interface UseAutoScrollReturn {
  /** Whether the user has scrolled away from the bottom */
  isScrolledUp: boolean;
  /** Call when scroll event fires on the container */
  handleScroll: () => void;
  /** Jump to bottom (called by scroll-to-bottom FAB) */
  scrollToBottom: () => void;
  /** Reset scroll state (call after sending a message) */
  resetScroll: () => void;
}

/**
 * Manages auto-scroll behavior for a chat container.
 *
 * - Tracks whether the user has scrolled up from the bottom
 * - Auto-scrolls to bottom when new content arrives (if not scrolled up)
 * - Clamps overscroll when content shrinks (tab switch, dropdown close)
 * - Preserves scroll position when the container resizes (keyboard open/close)
 * - Provides scrollToBottom() for the FAB
 */
export function useAutoScroll(
  scrollRef: RefObject<HTMLDivElement | null>,
  deps: unknown[],
  options: UseAutoScrollOptions = {},
): UseAutoScrollReturn {
  const { threshold = 80, smooth = true, isKeyboardTransitioning = false } = options;
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const userScrolledRef = useRef(false);
  // Track distance from bottom so we can restore it after container resize
  const distFromBottomRef = useRef(0);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    distFromBottomRef.current = distFromBottom;

    const nearBottom = distFromBottom < threshold;
    userScrolledRef.current = !nearBottom;
    setIsScrolledUp(!nearBottom);
  }, [scrollRef, threshold]);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    userScrolledRef.current = false;
    setIsScrolledUp(false);

    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth ? "smooth" : "instant",
    });
  }, [scrollRef, smooth]);

  const resetScroll = useCallback(() => {
    userScrolledRef.current = false;
    setIsScrolledUp(false);
  }, []);

  // ── Preserve scroll position on container resize (keyboard open/close) ──
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let prevHeight = el.clientHeight;

    const observer = new ResizeObserver(() => {
      const newHeight = el.clientHeight;
      if (newHeight === prevHeight) return;

      // Container height changed (keyboard opened/closed).
      // Restore the same distance-from-bottom so visible messages don't shift.
      const savedDist = distFromBottomRef.current;
      const newScrollTop = el.scrollHeight - newHeight - savedDist;
      el.scrollTop = Math.max(0, newScrollTop);

      prevHeight = newHeight;
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollRef]);

  // ── Track content height changes and adjust scroll position ──
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let prevScrollHeight = el.scrollHeight;
    let rafId: number | null = null;
    let ticks = 0;
    let smoothScrollUntil = 0;

    const check = () => {
      const now = performance.now();

      // Don't interfere while a smooth scroll is animating
      if (now < smoothScrollUntil) {
        ticks++;
        if (ticks < 30) rafId = requestAnimationFrame(check);
        else rafId = null;
        return;
      }

      const newScrollHeight = el.scrollHeight;
      const maxScroll = newScrollHeight - el.clientHeight;
      const shrunk = newScrollHeight < prevScrollHeight - 2;

      // Content shrunk (tab switch, etc.) — one smooth scroll
      if (shrunk && el.scrollTop > maxScroll + 1) {
        prevScrollHeight = newScrollHeight;
        smoothScrollUntil = now + 400;
        el.scrollTo({ top: Math.max(0, maxScroll), behavior: "smooth" });
        ticks++;
        if (ticks < 30) rafId = requestAnimationFrame(check);
        else rafId = null;
        return;
      }

      // Content grew or stayed same — pin to bottom
      if (!userScrolledRef.current && !isKeyboardTransitioning) {
        const distFromBottom = newScrollHeight - el.scrollTop - el.clientHeight;
        if (distFromBottom > 1) {
          el.scrollTop = maxScroll;
        }
      }

      prevScrollHeight = newScrollHeight;
      ticks++;
      if (ticks < 30) {
        rafId = requestAnimationFrame(check);
      } else {
        rafId = null;
      }
    };

    const onMutation = () => {
      ticks = 0;
      if (rafId == null) rafId = requestAnimationFrame(check);
    };

    const observer = new MutationObserver(onMutation);
    observer.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ["style", "class"] });

    return () => {
      observer.disconnect();
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [scrollRef, smooth, isKeyboardTransitioning]);

  // Auto-scroll when deps change (new messages, streaming content)
  useEffect(() => {
    // Don't auto-scroll during keyboard transitions — the ResizeObserver
    // handles preserving position. Only scroll for actual content changes.
    if (isKeyboardTransitioning) return;

    if (!userScrolledRef.current && scrollRef.current) {
      const el = scrollRef.current;
      // Use smooth for small increments (streaming), instant for big jumps (initial load)
      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const behavior = smooth && distanceToBottom < 500 ? "smooth" : "instant";
      el.scrollTo({ top: el.scrollHeight, behavior });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, isKeyboardTransitioning]);

  return { isScrolledUp, handleScroll, scrollToBottom, resetScroll };
}
